import React, { useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import { Grid, makeStyles } from '@material-ui/core'
import SydButton from '../../../../../commonDesign/Button'
import SydModal from '../../../../../commonDesign/SydModal'
import SydModalActions from '../../../../../commonDesign/SydModal/SydModalActions'
import FlexRow from '../../../../../molecules/FlexRow'

const useStyles = makeStyles((theme) => ({
  section: {
    border: 'var(--border-neutral-thin)',
    borderRadius: 'var(--border-radius-md)',
    padding: theme.layout.padding.p10,
    marginBottom: theme.layout.gap.g5
  },
  sectionTitle: {
    fontSize: '1rem',
    fontWeight: 600,
    marginBottom: theme.layout.gap.g5
  },
  label: {
    fontWeight: 500,
    marginBottom: '4px',
    display: 'block'
  },
  // TODO: extract infoBanner variants (danger, warning, success) to useStyles
  infoBanner: {
    padding: theme.layout.padding.p10,
    background: 'var(--color-surface-secondary)',
    borderRadius: 'var(--border-radius-sm)',
    fontSize: '0.875rem',
    lineHeight: 1.5
  },
  jsonPreview: {
    background: 'var(--color-surface-secondary)',
    borderRadius: 'var(--border-radius-sm)',
    padding: theme.layout.padding.p10,
    fontFamily: 'monospace',
    fontSize: '0.75rem',
    maxHeight: '400px',
    overflow: 'auto',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-all'
  },
  hashDisplay: {
    fontFamily: 'monospace',
    fontSize: '0.75rem',
    wordBreak: 'break-all',
    color: 'var(--color-text-secondary)'
  },
  modalBody: {
    fontSize: '0.875rem',
    lineHeight: 1.6
  },
  modalStrong: {
    fontWeight: 600,
    display: 'block',
    marginTop: '12px',
    marginBottom: '4px'
  },
  statusBadge: {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: 'var(--border-radius-sm)',
    fontSize: '0.75rem',
    fontWeight: 600
  },
  ddlBlock: {
    background: 'var(--color-surface-secondary)',
    borderRadius: 'var(--border-radius-sm)',
    padding: '8px 12px',
    fontFamily: 'monospace',
    fontSize: '0.75rem',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-all',
    marginBottom: '8px'
  },
  previewSection: {
    marginTop: '12px',
    padding: theme.layout.padding.p10,
    border: 'var(--border-neutral-thin)',
    borderRadius: 'var(--border-radius-sm)'
  },
  warningItem: {
    fontSize: '0.8125rem',
    padding: '4px 0',
    color: 'var(--color-warning-700)'
  }
}))

function getStatusColor (status) {
  switch (status) {
    case 'up_to_date': return 'var(--color-success-500)'
    case 'out_of_sync': return 'var(--color-warning-500)'
    case 'not_exists': return 'var(--color-danger-500)'
    case 'unmanaged': return 'var(--color-warning-500)'
    default: return 'var(--color-text-secondary)'
  }
}

function getStatusLabel (status) {
  switch (status) {
    case 'up_to_date': return 'Up to date'
    case 'out_of_sync': return 'Migration pending'
    case 'not_exists': return 'Table does not exist'
    case 'unmanaged': return 'Unmanaged'
    default: return status
  }
}

function MigrationPreview ({ result, classes }) {
  const { status, needsMigration, summary, safety, migrations, hashes } = result

  return (
    <div className={classes.previewSection}>
      <FlexRow style={{ gap: '12px', alignItems: 'center', marginBottom: '8px' }}>
        <span
          className={classes.statusBadge}
          style={{ backgroundColor: getStatusColor(status), color: '#fff' }}
        >
          {getStatusLabel(status)}
        </span>
        <span style={{ fontSize: '0.875rem' }}>{summary}</span>
      </FlexRow>

      {hashes && (
        <div style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)', marginBottom: '8px' }}>
          Configured: <code>{hashes.configured ? `${hashes.configured.slice(0, 12)}...` : '(none)'}</code>
          {' | '}Stored: <code>{hashes.stored ? `${hashes.stored.slice(0, 12)}...` : '(none)'}</code>
        </div>
      )}

      {safety && !safety.safe && (
        <div
          style={{
            padding: '8px 12px',
            borderLeft: '3px solid var(--color-danger-500)',
            background: 'var(--color-surface-secondary)',
            borderRadius: 'var(--border-radius-sm)',
            marginBottom: '8px'
          }}
        >
          <strong style={{ color: 'var(--color-danger-500)' }}>Data loss risk detected</strong>
        </div>
      )}

      {safety?.warnings?.length > 0 && (
        <div style={{ marginBottom: '8px' }}>
          {safety.warnings.map((w, i) => (
            <div key={i} className={classes.warningItem}>{w}</div>
          ))}
        </div>
      )}

      {needsMigration && migrations?.length > 0 && (
        <div>
          <label style={{ fontWeight: 500, fontSize: '0.8125rem', display: 'block', marginBottom: '6px' }}>
            DDL statements ({migrations.length})
          </label>
          {migrations.map((m, i) => (
            <div key={i} className={classes.ddlBlock}>
              <div style={{ fontWeight: 600, marginBottom: '4px', fontSize: '0.6875rem', textTransform: 'uppercase', color: 'var(--color-text-secondary)' }}>
                {m.operation}
              </div>
              {m.ddl}
            </div>
          ))}
        </div>
      )}

      {!needsMigration && (
        <div style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>
          No migrations will be applied on the next batch run.
        </div>
      )}
    </div>
  )
}

MigrationPreview.propTypes = {
  result: PropTypes.shape({
    status: PropTypes.string,
    needsMigration: PropTypes.bool,
    summary: PropTypes.string,
    safety: PropTypes.shape({
      safe: PropTypes.bool,
      warnings: PropTypes.arrayOf(PropTypes.string),
      dataLossRisk: PropTypes.bool
    }),
    migrations: PropTypes.arrayOf(PropTypes.shape({
      operation: PropTypes.string,
      ddl: PropTypes.string
    })),
    hashes: PropTypes.shape({
      configured: PropTypes.string,
      stored: PropTypes.string
    })
  }).isRequired,
  classes: PropTypes.object.isRequired
}

function TroubleshootingTab ({ form, isReconciling, isPreviewingMigration, isApplyingMigration, resetMigrationHash, reconcile, previewMigration, applyMigration }) {
  const classes = useStyles()
  // TODO: extract modal state machines to useTroubleshootingModals hook
  const [showJson, setShowJson] = useState(false)
  const [resetModalOpen, setResetModalOpen] = useState(false)
  const [previewModalOpen, setPreviewModalOpen] = useState(false)
  const [previewResult, setPreviewResult] = useState(null)
  const [applyResult, setApplyResult] = useState(null)
  const [confirmingDataLoss, setConfirmingDataLoss] = useState(false)
  const [reconcileModalOpen, setReconcileModalOpen] = useState(false)
  const [reconcileStage, setReconcileStage] = useState('confirm')
  const [reconcileReport, setReconcileReport] = useState(null)
  const [isApplying, setIsApplying] = useState(false)
  const [applied, setApplied] = useState(false)
  const [error, setError] = useState(null)

  const handleResetConfirm = useCallback(() => {
    resetMigrationHash()
    setResetModalOpen(false)
  }, [resetMigrationHash])

  const handleReconcileOpen = useCallback(() => {
    setReconcileStage('confirm')
    setReconcileReport(null)
    setApplied(false)
    setError(null)
    setReconcileModalOpen(true)
  }, [])

  const handleRunCheck = useCallback(async () => {
    setError(null)
    try {
      const result = await reconcile(true)
      setReconcileReport(result)
      setReconcileStage('report')
    } catch (err) {
      console.error('Reconcile dry run failed', err)
      setError(err.message || 'Reconcile check failed')
      setReconcileStage('report')
    }
  }, [reconcile])

  const handleApply = useCallback(async () => {
    setError(null)
    try {
      setIsApplying(true)
      const result = await reconcile(false)
      setReconcileReport(result)
      setApplied(true)
    } catch (err) {
      console.error('Reconcile apply failed', err)
      setError(err.message || 'Reconcile apply failed')
    } finally {
      setIsApplying(false)
    }
  }, [reconcile])

  const handleReconcileClose = useCallback(() => {
    setReconcileModalOpen(false)
  }, [])

  const handlePreviewOpen = useCallback(async () => {
    setPreviewResult(null)
    setApplyResult(null)
    setConfirmingDataLoss(false)
    setError(null)
    setPreviewModalOpen(true)
    try {
      const result = await previewMigration()
      setPreviewResult(result)
    } catch (err) {
      console.error('Preview migration failed', err)
      setError(err.message || 'Failed to load migration preview')
    }
  }, [previewMigration])

  const handlePreviewClose = useCallback(() => {
    setPreviewModalOpen(false)
  }, [])

  const handleApplyMigration = useCallback(async () => {
    if (previewResult?.safety?.dataLossRisk && !confirmingDataLoss) {
      setConfirmingDataLoss(true)
      return
    }
    setError(null)
    try {
      const result = await applyMigration()
      setApplyResult(result)
    } catch (err) {
      console.error('Apply migration failed', err)
      setError(err.message || 'Failed to apply migration')
      setConfirmingDataLoss(false)
    }
  }, [applyMigration, previewResult, confirmingDataLoss])

  const hasDrift = reconcileReport?.status === 'drift_detected'
  const isInSync = reconcileReport?.status === 'in_sync'

  return (
    <Grid container spacing={2}>
      {/* Info Banner */}
      <Grid item xs={12}>
        <div className={classes.infoBanner}>
          Column and index changes are applied automatically on the next batch run.
          The tools below are for <strong>troubleshooting only</strong> &mdash; you do not need
          them for normal configuration changes.
        </div>
      </Grid>

      {/* Migration Management */}
      <Grid item xs={12}>
        <div className={classes.section}>
          <div className={classes.sectionTitle}>Migration Management</div>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <label className={classes.label}>Current Migration Hash</label>
              <div className={classes.hashDisplay}>
                {form.configuration.migrationHash || '(none)'}
              </div>
            </Grid>
            <Grid item xs={12}>
              <FlexRow style={{ gap: '8px' }}>
                <SydButton
                  variant='outline'
                  size='sm'
                  onClick={handlePreviewOpen}
                  disabled={isPreviewingMigration}
                >
                  Preview Migration
                </SydButton>
                <SydButton variant='outline' size='sm' onClick={() => setResetModalOpen(true)}>
                  Reset Migration Hash
                </SydButton>
                <SydButton
                  variant='outline'
                  size='sm'
                  onClick={handleReconcileOpen}
                  disabled={isReconciling}
                >
                  Reconcile Metadata
                </SydButton>
              </FlexRow>
            </Grid>
          </Grid>
        </div>
      </Grid>

      {/* JSON Preview */}
      <Grid item xs={12}>
        <div className={classes.section}>
          <FlexRow style={{ justifyContent: 'space-between', alignItems: 'center' }}>
            <div className={classes.sectionTitle} style={{ marginBottom: 0 }}>JSON Preview</div>
            <SydButton variant='outline' size='sm' onClick={() => setShowJson(!showJson)}>
              {showJson ? 'Hide' : 'Show'}
            </SydButton>
          </FlexRow>
          {showJson && (
            <div className={classes.jsonPreview} style={{ marginTop: '8px' }}>
              {JSON.stringify({
                timeSeriesId: form.timeSeriesId,
                name: form.name,
                description: form.description || null,
                configuration: form.configuration,
                enabled: form.enabled
              }, null, 2)}
            </div>
          )}
        </div>
      </Grid>

      {/* Preview Migration Modal */}
      <SydModal
        open={previewModalOpen}
        onClose={handlePreviewClose}
        title='Preview Migration'
        size={previewResult ? 'medium' : 'small'}
      >
        {!previewResult && !applyResult && !error && (
          <div className={classes.modalBody}>
            <p>Loading migration preview&hellip;</p>
          </div>
        )}
        {!previewResult && !applyResult && error && (
          <div className={classes.modalBody}>
            <div
              className={classes.infoBanner}
              style={{ borderLeft: '3px solid var(--color-danger-500)' }}
            >
              {error}
            </div>
          </div>
        )}
        {previewResult && !applyResult && (
          <>
            <MigrationPreview result={previewResult} classes={classes} />
            {confirmingDataLoss && (
              <div
                className={classes.infoBanner}
                style={{ borderLeft: '3px solid var(--color-danger-500)', marginTop: '12px' }}
              >
                This migration involves <strong>data loss risk</strong>. Are you sure you want
                to proceed? Click &ldquo;Apply Migration&rdquo; again to confirm.
              </div>
            )}
            {error && (
              <div
                className={classes.infoBanner}
                style={{ borderLeft: '3px solid var(--color-danger-500)', marginTop: '12px' }}
              >
                {error}
              </div>
            )}
          </>
        )}
        {applyResult && (
          <div className={classes.modalBody}>
            <div
              className={classes.infoBanner}
              style={{ borderLeft: '3px solid var(--color-success-500)' }}
            >
              Migration batch started successfully. You can monitor progress in the <strong>Runs</strong> tab.
            </div>
            {applyResult.batchId && (
              <div style={{ marginTop: '12px', fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
                Batch ID: <code>{applyResult.batchId}</code>
              </div>
            )}
          </div>
        )}
        <SydModalActions>
          <SydButton variant='outline' size='sm' onClick={handlePreviewClose}>
            {applyResult ? 'Done' : 'Close'}
          </SydButton>
          {previewResult?.needsMigration && !applyResult && (
            <SydButton
              variant='primary'
              size='sm'
              onClick={handleApplyMigration}
              disabled={isApplyingMigration}
            >
              {isApplyingMigration ? 'Starting...' : 'Apply Migration'}
            </SydButton>
          )}
        </SydModalActions>
      </SydModal>

      {/* Reset Migration Hash Modal */}
      <SydModal
        open={resetModalOpen}
        onClose={() => setResetModalOpen(false)}
        title='Reset Migration Hash'
        size='small'
      >
        <div className={classes.modalBody}>
          <p>
            This clears the stored migration hash, causing the system to treat the table
            schema as unknown on the next batch run. A full table migration check will be performed.
          </p>
          <span className={classes.modalStrong}>When to use this:</span>
          <p>
            Only if you suspect the stored hash is corrupt or out of sync with the actual
            table state. You do <strong>not</strong> need this to apply column or index
            changes &mdash; those are detected and applied automatically.
          </p>
        </div>
        <SydModalActions>
          <SydButton variant='outline' size='sm' onClick={() => setResetModalOpen(false)}>
            Cancel
          </SydButton>
          <SydButton variant='primary' size='sm' onClick={handleResetConfirm}>
            Reset
          </SydButton>
        </SydModalActions>
      </SydModal>

      {/* Reconcile Metadata Modal */}
      <SydModal
        open={reconcileModalOpen}
        onClose={handleReconcileClose}
        title='Reconcile Metadata'
        size={reconcileStage === 'report' ? 'medium' : 'small'}
      >
        {reconcileStage === 'confirm' && (
          <>
            <div className={classes.modalBody}>
              <p>
                This compares the stored table metadata against the actual database schema
                and produces a drift report. No changes are made until you explicitly apply them.
              </p>
              <span className={classes.modalStrong}>When to use this:</span>
              <p>
                If a migration failed or the table was manually altered outside of this tool.
                This helps diagnose whether the stored metadata matches reality.
                You do <strong>not</strong> need this to apply column or index changes &mdash;
                those are detected and applied automatically.
              </p>
            </div>
            <SydModalActions>
              <SydButton variant='outline' size='sm' onClick={handleReconcileClose}>
                Cancel
              </SydButton>
              <SydButton
                variant='primary'
                size='sm'
                onClick={handleRunCheck}
                disabled={isReconciling}
              >
                {isReconciling ? 'Checking...' : 'Run Check'}
              </SydButton>
            </SydModalActions>
          </>
        )}

        {reconcileStage === 'report' && (
          <>
            <div className={classes.modalBody}>
              {error && (
                <div
                  className={classes.infoBanner}
                  style={{ borderLeft: '3px solid var(--color-danger-500)', marginBottom: '12px' }}
                >
                  {error}
                </div>
              )}
              {isInSync && !applied && (
                <div className={classes.infoBanner}>
                  All schemas are in sync. No action needed.
                </div>
              )}
              {hasDrift && !applied && (
                <div className={classes.infoBanner} style={{ borderLeft: '3px solid var(--color-warning-500)' }}>
                  Drift detected between stored metadata and the actual database schema.
                  Review the report below, then apply if you want to update the stored metadata
                  to match the current database state.
                </div>
              )}
              {applied && (
                <div className={classes.infoBanner} style={{ borderLeft: '3px solid var(--color-success-500)' }}>
                  Reconciliation applied. Stored metadata has been updated to match the actual database schema.
                </div>
              )}
              {reconcileReport && (
                <div className={classes.jsonPreview} style={{ marginTop: '12px' }}>
                  {JSON.stringify(reconcileReport, null, 2)}
                </div>
              )}
            </div>
            <SydModalActions>
              <SydButton variant='outline' size='sm' onClick={handleReconcileClose}>
                Close
              </SydButton>
              {hasDrift && !applied && (
                <SydButton
                  variant='primary'
                  size='sm'
                  onClick={handleApply}
                  disabled={isApplying}
                >
                  {isApplying ? 'Applying...' : 'Apply Reconciliation'}
                </SydButton>
              )}
            </SydModalActions>
          </>
        )}
      </SydModal>
    </Grid>
  )
}

TroubleshootingTab.propTypes = {
  form: PropTypes.object.isRequired,
  isReconciling: PropTypes.bool,
  isPreviewingMigration: PropTypes.bool,
  isApplyingMigration: PropTypes.bool,
  resetMigrationHash: PropTypes.func.isRequired,
  reconcile: PropTypes.func.isRequired,
  previewMigration: PropTypes.func.isRequired,
  applyMigration: PropTypes.func.isRequired
}

export default TroubleshootingTab
